import React, { useCallback, useMemo } from "react";
import styled from "styled-components";

export enum DayOfWeek {
  MONDAY = "MONDAY",
  TUESDAY = "TUESDAY",
  WEDNESDAY = "WEDNESDAY",
  THURSDAY = "THURSDAY",
  FRIDAY = "FRIDAY",
  SATURDAY = "SATURDAY",
  SUNDAY = "SUNDAY",
}

// ISO week order: Monday = 0 ... Sunday = 6
const DAY_ORDER: DayOfWeek[] = [
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
  DayOfWeek.SUNDAY,
];

const REFERENCE_DATES: Record<DayOfWeek, Date> = {
  [DayOfWeek.MONDAY]: new Date(Date.UTC(2025, 0, 6)),
  [DayOfWeek.TUESDAY]: new Date(Date.UTC(2025, 0, 7)),
  [DayOfWeek.WEDNESDAY]: new Date(Date.UTC(2025, 0, 8)),
  [DayOfWeek.THURSDAY]: new Date(Date.UTC(2025, 0, 9)),
  [DayOfWeek.FRIDAY]: new Date(Date.UTC(2025, 0, 10)),
  [DayOfWeek.SATURDAY]: new Date(Date.UTC(2025, 0, 11)),
  [DayOfWeek.SUNDAY]: new Date(Date.UTC(2025, 0, 12)),
};

// --- Styled components ---

const DaysRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  flex-wrap: wrap;
`;

const DayButton = styled.button<{ $active: boolean; $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 4px;
  border: 1px solid ${({ $active }) => ($active ? "#ff6136" : "#ced4da")};
  background-color: ${({ $active }) => ($active ? "#ff6136" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#495057")};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.2s ease;
  user-select: none;

  &:hover:not(:disabled) {
    border-color: #ff6136;
    color: ${({ $active }) => ($active ? "#ffffff" : "#ff6136")};
    transform: scale(1.1);
  }

  &:focus {
    outline-color: #ff6136;
  }

  &:focus-visible {
    outline: 2px solid #ff6136;
    outline-offset: 2px;
  }
`;

// --- Component ---

export interface DaysOfWeekSelectorProps {
  value: DayOfWeek[];
  onChange: (value: DayOfWeek[]) => void;
  locale?: string;
  disabled?: boolean;
}

export const DaysOfWeekSelector = ({
  value,
  onChange,
  locale,
  disabled = false,
}: DaysOfWeekSelectorProps): JSX.Element => {
  const resolvedLocale = locale ?? navigator.language;

  const labels = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(resolvedLocale, {
      weekday: "narrow",
      timeZone: "UTC",
    });
    return DAY_ORDER.reduce<Record<DayOfWeek, string>>((acc, day) => {
      acc[day] = formatter.format(REFERENCE_DATES[day]);
      return acc;
    }, {} as Record<DayOfWeek, string>);
  }, [resolvedLocale]);

  const longLabels = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(resolvedLocale, { weekday: "long", timeZone: "UTC" });
    return DAY_ORDER.reduce<Record<DayOfWeek, string>>((acc, day) => {
      acc[day] = formatter.format(REFERENCE_DATES[day]);
      return acc;
    }, {} as Record<DayOfWeek, string>);
  }, [resolvedLocale]);

  const handleToggle = useCallback(
    (day: DayOfWeek) => {
      if (disabled) return;
      const isSelected = value.includes(day);
      const next = isSelected ? value.filter(d => d !== day) : [...value, day];
      onChange(next);
    },
    [disabled, value, onChange]
  );

  return (
    <DaysRow role="group">
      {DAY_ORDER.map(day => {
        const active = value.includes(day);
        return (
          <DayButton
            key={day}
            type="button"
            $active={active}
            $disabled={disabled}
            disabled={disabled}
            aria-pressed={active}
            aria-label={longLabels[day]}
            title={longLabels[day]}
            onClick={() => handleToggle(day)}>
            {labels[day]}
          </DayButton>
        );
      })}
    </DaysRow>
  );
};
